import { SelectOption } from '../generic-section/generic-section.component';

/** How the currency unit is shown before/after the amount. */
export type ProductCardCurrencyDisplayStyle = 'symbol' | 'code' | 'name';

export interface ParsedProductCardCurrencyFormat {
  currencyCode: string;
  displayStyle: ProductCardCurrencyDisplayStyle;
}

const FALLBACK_CURRENCY_CODES: readonly string[] = [
  'AED', 'AFN', 'ALL', 'AMD', 'ANG', 'AOA', 'ARS', 'AUD', 'AWG', 'AZN',
  'BAM', 'BBD', 'BDT', 'BGN', 'BHD', 'BIF', 'BMD', 'BND', 'BOB', 'BRL', 'BSD', 'BTN', 'BWP', 'BYN', 'BZD',
  'CAD', 'CDF', 'CHF', 'CLP', 'CNY', 'COP', 'CRC', 'CUP', 'CVE', 'CZK',
  'DJF', 'DKK', 'DOP', 'DZD',
  'EGP', 'ERN', 'ETB', 'EUR',
  'FJD', 'FKP',
  'GBP', 'GEL', 'GHS', 'GIP', 'GMD', 'GNF', 'GTQ', 'GYD',
  'HKD', 'HNL', 'HRK', 'HTG', 'HUF',
  'IDR', 'ILS', 'INR', 'IQD', 'IRR', 'ISK',
  'JMD', 'JOD', 'JPY',
  'KES', 'KGS', 'KHR', 'KMF', 'KPW', 'KRW', 'KWD', 'KYD', 'KZT',
  'LAK', 'LBP', 'LKR', 'LRD', 'LSL', 'LYD',
  'MAD', 'MDL', 'MGA', 'MKD', 'MMK', 'MNT', 'MOP', 'MRU', 'MUR', 'MVR', 'MWK', 'MXN', 'MYR', 'MZN',
  'NAD', 'NGN', 'NIO', 'NOK', 'NPR', 'NZD',
  'OMR',
  'PAB', 'PEN', 'PGK', 'PHP', 'PKR', 'PLN', 'PYG',
  'QAR',
  'RON', 'RSD', 'RUB', 'RWF',
  'SAR', 'SBD', 'SCR', 'SDG', 'SEK', 'SGD', 'SHP', 'SLE', 'SOS', 'SRD', 'SSP', 'STN', 'SYP', 'SZL',
  'THB', 'TJS', 'TMT', 'TND', 'TOP', 'TRY', 'TTD', 'TWD', 'TZS',
  'UAH', 'UGX', 'USD', 'UYU', 'UZS',
  'VES', 'VND', 'VUV',
  'WST',
  'XAF', 'XCD', 'XOF', 'XPF',
  'YER',
  'ZAR', 'ZMW', 'ZWL'
];

const DISPLAY_STYLES: ReadonlyArray<{
  id: ProductCardCurrencyDisplayStyle;
  label: (code: string, symbol: string, name: string) => string;
}> = [
  {
    id: 'symbol',
    label: (_code, symbol) => `Symbol (${symbol})`
  },
  {
    id: 'code',
    label: (code) => `ISO code (${code})`
  },
  {
    id: 'name',
    label: (_code, _symbol, name) => `Full name (${name})`
  }
];

let cachedFormatOptions: SelectOption[] | null = null;

export function getProductCardCurrencyCodes(): string[] {
  if (typeof Intl !== 'undefined' && 'supportedValuesOf' in Intl) {
    try {
      const supported = (Intl as unknown as { supportedValuesOf(key: string): string[] }).supportedValuesOf(
        'currency'
      );
      if (supported?.length) {
        return [...supported].sort((a, b) => a.localeCompare(b));
      }
    } catch {
      /* use fallback */
    }
  }
  return [...FALLBACK_CURRENCY_CODES];
}

export function getProductCardCurrencySymbol(code: string, locale = 'en'): string {
  const normalized = String(code ?? '').trim();
  if (!normalized) {
    return '';
  }
  try {
    const part = new Intl.NumberFormat(locale, { style: 'currency', currency: normalized })
      .formatToParts(0)
      .find((p) => p.type === 'currency');
    return part?.value?.trim() || normalized;
  } catch {
    return normalized;
  }
}

/** Product currency from General_Information; empty string when missing or blank. */
export function resolveProductCardProductCurrencyCode(
  product: { General_Information?: { currency?: string | null } } | null | undefined
): string {
  const raw = product?.General_Information?.currency;
  if (raw == null) {
    return '';
  }
  const normalized = String(raw).trim();
  return normalized ? normalized.toUpperCase() : '';
}

/** Display unit for a product currency code (EUR → €, USD → $, etc.); empty when code is missing. */
export function formatProductCardCurrencyDisplayUnit(currency: string | null | undefined): string {
  const code = String(currency ?? '').trim();
  if (!code) {
    return '';
  }
  const upper = code.toUpperCase();
  if (upper === 'EUR') {
    return '€';
  }
  if (upper === 'USD') {
    return '$';
  }
  return code;
}

export function getProductCardCurrencyName(code: string, locale = 'en'): string {
  try {
    const name = new Intl.DisplayNames([locale], { type: 'currency' }).of(code);
    return name?.trim() || code;
  } catch {
    return code;
  }
}

function parseDisplayStyle(token: string | undefined): ProductCardCurrencyDisplayStyle {
  return token === 'symbol' || token === 'code' || token === 'name' ? token : 'symbol';
}

/** Parses `EUR_symbol`, legacy `EUR_symbol_2`, `EUR_2`, or partial values. */
/** Extracts display style only; ignores any currency code embedded in legacy values like `EUR_symbol`. */
export function parseProductCardCurrencyDisplayStyle(
  value: string | null | undefined
): ProductCardCurrencyDisplayStyle {
  const raw = String(value ?? '').trim();
  if (raw === 'symbol' || raw === 'code' || raw === 'name') {
    return raw;
  }
  return parseProductCardCurrencyFormat(raw).displayStyle;
}

/** Normalizes legacy `EUR_symbol` tokens to style-only values (`symbol`, `code`, `name`). */
export function normalizeProductCardCurrencyFormat(
  value: string | null | undefined
): ProductCardCurrencyDisplayStyle {
  return parseProductCardCurrencyDisplayStyle(value);
}

export function parseProductCardCurrencyFormat(
  value: string | null | undefined
): ParsedProductCardCurrencyFormat {
  const raw = String(value ?? '').trim();
  if (!raw) {
    return { currencyCode: 'EUR', displayStyle: 'symbol' };
  }

  const parts = raw.split('_').filter(Boolean);
  if (parts.length === 2) {
    const styleToken = parts[1];
    if (styleToken === 'symbol' || styleToken === 'code' || styleToken === 'name') {
      return {
        currencyCode: parts[0].toUpperCase(),
        displayStyle: styleToken
      };
    }

    return {
      currencyCode: parts[0].toUpperCase(),
      displayStyle: 'code'
    };
  }

  if (parts.length >= 3) {
    const styleToken = parts[parts.length - 2];
    const currencyCode = parts.slice(0, parts.length - 2).join('_').toUpperCase();
    return {
      currencyCode: currencyCode || 'EUR',
      displayStyle: parseDisplayStyle(styleToken)
    };
  }

  return { currencyCode: raw.toUpperCase(), displayStyle: 'symbol' };
}

export function formatProductCardCurrencyFormatValue(
  currencyCode: string,
  displayStyle: ProductCardCurrencyDisplayStyle
): string {
  return `${currencyCode.toUpperCase()}_${displayStyle}`;
}

export function formatProductCardPriceAmount(price: number | string, locale = 'en'): string {
  if (typeof price === 'string') {
    const trimmed = price.trim();
    if (trimmed === '') {
      return '0';
    }
    const numeric = Number(trimmed);
    if (!Number.isFinite(numeric)) {
      return '0';
    }
    // Keep API decimal precision for plain numeric strings (e.g. "7.0000").
    if (/^-?\d+(\.\d+)?$/.test(trimmed)) {
      return trimmed;
    }
    return numeric.toLocaleString(locale, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 10
    });
  }

  if (!Number.isFinite(price)) {
    return '0';
  }

  return price.toLocaleString(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 10
  });
}

export function resolveProductCardCurrencyUnit(
  parsed: ParsedProductCardCurrencyFormat,
  customSymbol?: string | null
): string {
  const code = parsed.currencyCode;
  switch (parsed.displayStyle) {
    case 'code':
      return code;
    case 'name':
      return getProductCardCurrencyName(code);
    case 'symbol':
    default:
      return customSymbol?.trim() || getProductCardCurrencySymbol(code);
  }
}

/**
 * Resolves a KPI/admin currency display config to the unit shown next to amounts.
 * Accepts style tokens (`symbol`/`code`/`name`) or legacy literal symbols (`€`, `$`).
 */
export function resolveDashboardKpiCurrencyDisplay(
  value: string | null | undefined,
  currencyCode = 'EUR',
  locale = 'en'
): string {
  const raw = String(value ?? '').trim();
  if (!raw) {
    return getProductCardCurrencySymbol(currencyCode, locale);
  }
  if (raw === 'symbol' || raw === 'code' || raw === 'name') {
    return resolveProductCardCurrencyUnit(
      { currencyCode: currencyCode.toUpperCase(), displayStyle: raw },
      null
    );
  }
  return raw;
}

/** Display-style options for admin (currency always comes from the product at runtime). */
export function buildProductCardCurrencyDisplayStyleOptions(): SelectOption[] {
  return DISPLAY_STYLES.map((style) => ({
    label: style.label('XXX', '¤', 'currency'),
    value: style.id
  }));
}

/** All currency × display style combinations (searchable list). */
export function buildProductCardCurrencyFormatOptions(locale = 'en'): SelectOption[] {
  if (cachedFormatOptions) {
    return cachedFormatOptions;
  }

  const options: SelectOption[] = [];

  for (const code of getProductCardCurrencyCodes()) {
    let symbol: string;
    let name: string;
    try {
      symbol = getProductCardCurrencySymbol(code, locale);
      name = getProductCardCurrencyName(code, locale);
    } catch {
      continue;
    }

    for (const style of DISPLAY_STYLES) {
      options.push({
        label: `${name} (${code}) · ${style.label(code, symbol, name)}`,
        value: formatProductCardCurrencyFormatValue(code, style.id)
      });
    }
  }

  cachedFormatOptions = options;
  return options;
}

/** Clears cached options (e.g. for tests). */
export function clearProductCardCurrencyFormatOptionsCache(): void {
  cachedFormatOptions = null;
}
